import { ReactNode, useState } from 'react';
import {
  RacingFilters,
  RacingRow,
  useExoticBets,
  useMeetingPerformance,
  useRacingFilterOptions,
  useRoiAnalysis,
  useTrackCondition,
  useTrackPerformance,
} from '../../queries/racingAnalytics';
import { DashboardTab, RoiDashboardTab } from './RacingDashboardTabs';
import { HighProbabilityRoiTab } from './HighProbabilityRoiTab';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';

const FILTER = 'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const TABS = ['Dashboard', 'Meeting Performance', 'Track Performance', 'Exotic Bets', 'ROI Analysis', 'Track Condition', 'ROI Dashboard', 'High-Probability ROI'] as const;
type Tab = (typeof TABS)[number];
const TABLE_TABS = ['Meeting Performance', 'Track Performance', 'Exotic Bets', 'ROI Analysis', 'Track Condition'] as const;
type TableTab = (typeof TABLE_TABS)[number];

const num = (v: RacingRow[string]) => (v == null ? 0 : Number(v));
const pct = (v: RacingRow[string]) => `${num(v).toFixed(2)}%`;
const int = (v: RacingRow[string]) => num(v).toLocaleString();
const DATE = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });

interface Column {
  header: string;
  render: (r: RacingRow) => ReactNode;
  align?: 'right';
}

function roiCell(v: RacingRow[string]) {
  const n = num(v);
  const tone = n > 0 ? 'text-emerald-600 dark:text-emerald-400' : n < 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground';
  return <span className={`font-medium ${tone}`}>{n > 0 ? '+' : ''}{n.toFixed(1)}%</span>;
}

const COLUMNS: Record<TableTab, Column[]> = {
  'Meeting Performance': [
    { header: 'Date', render: (r) => (r.meeting_date ? DATE.format(new Date(String(r.meeting_date))) : '—') },
    { header: 'Track', render: (r) => r.track_name },
    { header: 'State', render: (r) => String(r.state ?? '').toUpperCase() },
    { header: 'Cond.', render: (r) => r.track_condition ?? '—' },
    { header: 'Races', render: (r) => int(r.total_races), align: 'right' },
    { header: 'Top-1 W', render: (r) => int(r.top_1_winners), align: 'right' },
    { header: 'Top-1 %', render: (r) => pct(r.top_1_win_percentage), align: 'right' },
    { header: 'Value %', render: (r) => pct(r.value_win_percentage), align: 'right' },
    { header: 'Exactas', render: (r) => int(r.exactas), align: 'right' },
    { header: 'Trifectas', render: (r) => int(r.trifectas), align: 'right' },
  ],
  'Track Performance': [
    { header: 'Track', render: (r) => r.track_name },
    { header: 'State', render: (r) => String(r.state ?? '').toUpperCase() },
    { header: 'Races', render: (r) => int(r.total_races), align: 'right' },
    { header: 'Top-1 %', render: (r) => pct(r.top_1_win_percentage), align: 'right' },
    { header: 'Top-2 %', render: (r) => pct(r.top_2_win_percentage), align: 'right' },
    { header: 'Top-3 %', render: (r) => pct(r.top_3_win_percentage), align: 'right' },
    { header: 'Value %', render: (r) => pct(r.value_win_percentage), align: 'right' },
  ],
  'Exotic Bets': [
    { header: 'Track', render: (r) => r.track_name },
    { header: 'Races', render: (r) => int(r.total_races), align: 'right' },
    { header: 'Exacta %', render: (r) => pct(r.exacta_percentage), align: 'right' },
    { header: 'Quinella %', render: (r) => pct(r.quinella_percentage), align: 'right' },
    { header: 'Trifecta %', render: (r) => pct(r.trifecta_percentage), align: 'right' },
    { header: 'Box Trif %', render: (r) => pct(r.box_trifecta_percentage), align: 'right' },
    { header: 'First4 %', render: (r) => pct(r.first_four_percentage), align: 'right' },
  ],
  'ROI Analysis': [
    { header: 'Track', render: (r) => r.track_name },
    { header: 'Top-1 bets', render: (r) => int(r.top_1_picks), align: 'right' },
    { header: 'Top-1 W', render: (r) => int(r.top_1_winners), align: 'right' },
    { header: 'Top-1 ROI', render: (r) => roiCell(r.top_1_roi), align: 'right' },
    { header: 'Top-3 ROI', render: (r) => roiCell(r.top_3_roi), align: 'right' },
    { header: 'Value ROI', render: (r) => roiCell(r.value_roi), align: 'right' },
  ],
  'Track Condition': [
    { header: 'Condition', render: (r) => r.track_condition },
    { header: 'Races', render: (r) => int(r.total_races), align: 'right' },
    { header: 'Top-1 %', render: (r) => pct(r.top_1_win_percentage), align: 'right' },
    { header: 'Top-1 ROI', render: (r) => roiCell(r.top_1_roi), align: 'right' },
    { header: 'Value %', render: (r) => pct(r.value_win_percentage), align: 'right' },
    { header: 'Value ROI', render: (r) => roiCell(r.value_roi), align: 'right' },
  ],
};

export function RacingAnalyticsPage() {
  const [filters, setFilters] = useState<RacingFilters>({ analysisTypeId: 1 });
  const [tab, setTab] = useState<Tab>('Dashboard');
  const { data: options } = useRacingFilterOptions();

  const tableQueries: Record<TableTab, ReturnType<typeof useTrackPerformance>> = {
    'Meeting Performance': useMeetingPerformance(filters),
    'Track Performance': useTrackPerformance(filters),
    'Exotic Bets': useExoticBets(filters),
    'ROI Analysis': useRoiAnalysis(filters),
    'Track Condition': useTrackCondition(filters),
  };
  const isTableTab = (t: Tab): t is TableTab => (TABLE_TABS as readonly string[]).includes(t);

  const set = (patch: Partial<RacingFilters>) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div className="space-y-5">
      <PageHeader title="Racing Analytics" description="Tips performance and betting ROI across the reporting data." />

      <div className="flex flex-wrap items-center gap-2">
        <select value={filters.analysisTypeId} onChange={(e) => set({ analysisTypeId: Number(e.target.value) })} className={FILTER}>
          <option value={1}>Model 1 (primary)</option>
          <option value={2}>Model 2 (secondary)</option>
        </select>
        <select value={filters.trackType ?? ''} onChange={(e) => set({ trackType: e.target.value || undefined })} className={FILTER}>
          <option value="">All track types</option>
          {options?.trackTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filters.state ?? ''} onChange={(e) => set({ state: e.target.value || undefined })} className={FILTER}>
          <option value="">All states</option>
          {options?.states.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>
        <select value={filters.trackCode ?? ''} onChange={(e) => set({ trackCode: e.target.value || undefined })} className={FILTER}>
          <option value="">All tracks</option>
          {options?.tracks.map((t) => <option key={t.track_code} value={t.track_code}>{t.track_name}</option>)}
        </select>
        <input type="date" value={filters.startDate ?? ''} onChange={(e) => set({ startDate: e.target.value || undefined })} className={FILTER} />
        <input type="date" value={filters.endDate ?? ''} onChange={(e) => set({ endDate: e.target.value || undefined })} className={FILTER} />
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-border pb-2">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tab === t ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Dashboard' && <DashboardTab analysisTypeId={filters.analysisTypeId} />}
      {tab === 'ROI Dashboard' && <RoiDashboardTab analysisTypeId={filters.analysisTypeId} />}
      {tab === 'High-Probability ROI' && <HighProbabilityRoiTab />}

      {isTableTab(tab) &&
        (tableQueries[tab].isLoading ? (
          <Spinner />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {COLUMNS[tab].map((c) => (
                    <th
                      key={c.header}
                      className={cn('whitespace-nowrap bg-muted/50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground', c.align === 'right' && 'text-right')}
                    >
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(tableQueries[tab].data ?? []).map((r, i) => (
                  <tr key={i} className="transition-colors hover:bg-muted/40">
                    {COLUMNS[tab].map((c) => (
                      <td key={c.header} className={cn('whitespace-nowrap px-3 py-2.5', c.align === 'right' && 'text-right tabular-nums')}>{c.render(r)}</td>
                    ))}
                  </tr>
                ))}
                {(tableQueries[tab].data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS[tab].length} className="px-3 py-10 text-center text-muted-foreground">No data for the current filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}
